/**
 * Profit Gap Card
 * Displays EBITDA gap with current and target values
 */
import React from 'react';
import PropTypes from 'prop-types';
import { PiggyBank, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';

const ProfitGapCard = ({ profitGap, setShowProfitGapDetails, handleRecalculateProfitGap, isViewer = false }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 p-4 text-white">
        <div className="flex items-center space-x-3">
          <PiggyBank size={24} />
          <div>
            <h3 className="text-lg font-bold">Profit Gap</h3>
            <p className="text-xs text-purple-100">EBITDA Performance</p>
          </div>
        </div>
      </div>
      <div className="p-4">
        {profitGap ? (
          <div className="space-y-3">
            {/* Current State */}
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Current Est. Business Exit Value:</span>
                <span className="font-bold text-gray-900">{formatCurrency(profitGap.current_valuation)}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-600">Current EBITDA Margin:</span>
                <span className="font-semibold text-amber-600">{profitGap.current_ebitda_margin.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-xs text-gray-600">Current Overall Business Gap Score:</span>
                <span className="font-semibold text-gray-700">{profitGap.assessment_score?.toFixed(1)}%</span>
              </div>
            </div>

            {/* Median Industry Margin Scenario */}
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-semibold text-blue-900 mb-2">If at Median Industry Margin ({profitGap.industry_median_ebitda_margin.toFixed(1)}%)</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-blue-700">Valuation Impact:</span>
                <span className="font-bold text-blue-600 text-lg">{formatCurrency(profitGap.valuation_impact_median)}</span>
              </div>
            </div>

            {/* Best in Class Margin Scenario */}
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs font-semibold text-green-900 mb-2">If at Best in Class Margin ({profitGap.industry_high_ebitda_margin.toFixed(1)}%)</p>
              <div className="flex justify-between items-center">
                <span className="text-xs text-green-700">Valuation Impact:</span>
                <span className="font-bold text-green-600 text-lg">{formatCurrency(profitGap.valuation_impact_best)}</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <button
                onClick={() => setShowProfitGapDetails(true)}
                className="flex-1 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-semibold transition text-sm flex items-center justify-center gap-1 min-h-[44px]"
              >
                <Info size={16} />
                Details
              </button>
              {!isViewer && (
                <button
                  onClick={handleRecalculateProfitGap}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition text-sm min-h-[44px]"
                >
                  Recalculate
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500 mb-3">Calculate valuation first</p>
            {!isViewer && (
              <button
                onClick={() => navigate('/valuation')}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold transition text-sm min-h-[44px]"
              >
                Calculate
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ProfitGapCard.propTypes = {
  profitGap: PropTypes.shape({
    current_ebitda: PropTypes.number,
    current_ebitda_margin: PropTypes.number,
    assessment_score: PropTypes.number,
    current_valuation: PropTypes.number,
    target_ebitda: PropTypes.number,
    ebitda_gap: PropTypes.number,
    valuation_gap: PropTypes.number,
    industry_median_ebitda_margin: PropTypes.number,
    industry_high_ebitda_margin: PropTypes.number,
    median_valuation: PropTypes.number,
    best_valuation: PropTypes.number,
    valuation_impact_median: PropTypes.number,
    valuation_impact_best: PropTypes.number
  }),
  setShowProfitGapDetails: PropTypes.func.isRequired,
  handleRecalculateProfitGap: PropTypes.func.isRequired,
  isViewer: PropTypes.bool
};

export default ProfitGapCard;
